import { connected, withErrorBoundary } from 'shared'
import { absolutizePath } from 'shared/util'
import { TopLevelPage } from 'ui'
import ParkCard from 'ui/parks/ParkCard'
import { EventApi, ParkApi } from 'api'
import { addDays, format, startOfMonth, endOfMonth } from 'date-fns'
import { useEffect, useState } from 'react'
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from "@fullcalendar/interaction"

let calendarGlobal = [];
let intervalId = null;

const CalendarView = ({ match }) => {
  const feedItemId = match.params.id;
  const [calendar, setCalendar] = useState([{ startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()), key: 'selection' }]);
  const [events, setEvents] = useState([]);
  const [openAvailable, setOpenAvailable] = useState([]);
  const [selectedPlaces, setSelectedPlaces] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [reservableDates, setReservableDates] = useState([]);


  const parseDate = (dateTime) => format(dateTime, "yyyy-MM-dd");

  const dateParams = (calendar) => ({ start_date: parseDate(calendar.startDate), end_date: parseDate(calendar.endDate) });

  const fetchReservableDates = () => {
    const start = new Date();
    const end = endOfMonth(new Date());
    const dates = [];

    for (let i = 0; i < (end - start) / (1000 * 60 * 60 * 24); i++) {
      const date = addDays(start, i);
      dates.push(format(date, "yyyy-MM-dd"));
    }

    Promise.all(dates.map(date => ParkApi.openAvailable({ start_date: date, end_date: date }).then(data => data.length > 0 ? { date, places: data } : null)))
      .then(results => setReservableDates(results.filter(Boolean)));
  };

  const handleReserveClick = (placeId, date) => {
    const newSelectedDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    setSelectedDate(newSelectedDate);

    const availablePlacesForDate = openAvailable.filter(place => {
      const days = Object.keys(place.open_periods || {});
      return days.some(day => {
        const today = newSelectedDate;
        const dayOffset = { sunday: 0, monday: 1, tuesday: 2, wednesday: 3, thursday: 4, friday: 5, saturday: 6 }[day.toLowerCase()];
        if (dayOffset == null) return false;
        const openDate = new Date(today);
        openDate.setDate(today.getDate() + ((dayOffset - today.getDay() + 7) % 7));
        return openDate.toISOString().split("T")[0] === newSelectedDate.toISOString().split("T")[0];
      });
    });

    setSelectedPlaces(availablePlacesForDate);
  };

  const loadData = (calendarArg = null) => {
    const activeCalendar = calendarArg || { startDate: startOfMonth(new Date()), endDate: endOfMonth(new Date()) };
    EventApi.filtered(dateParams(activeCalendar)).then(data => setEvents(data));
    ParkApi.openAvailable().then(data => setOpenAvailable(data));
    fetchReservableDates();
  };

  const handleDatesSet = (info) => {
    setCalendar([{ startDate: info.start, endDate: info.end, key: 'selection' }]);
    setSelectedDate(info.start);
  };

  useEffect(() => { loadData(calendar[0]); }, []);

  const calendarEvents = events.flatMap(model => {
    const base = "/app#/community-calendar";
    if (model.category === "multiday") {
      return { id: model.id, targetUid: model.targetUid, title: model.name, start: new Date(`${model.date}T09:00:00`).toISOString(), end: new Date(`${model.end_date}T18:00:00`).toISOString(), url: `${absolutizePath(base)}/${model.targetUid}`, allDay: true };
    } else if (model.is_recurring && model.occurrences) {
      return model.occurrences.map(occurrence => ({
        id: occurrence.id,
        targetUid: model.targetUid,
        title: model.name,
        start: new Date(`${occurrence.date}T${occurrence.time}:00`).toISOString(),
        end: new Date(`${occurrence.date}T${occurrence.close_time}:00`).toISOString(),
        allDay: false,
        url: `${absolutizePath(base)}/${model.targetUid}`
      }));
    } else {
      return { id: model.id, targetUid: model.targetUid, title: model.name, start: new Date(`${model.date}T${model.open_time_local}:00`).toISOString(), end: new Date(`${model.date}T${model.close_time_local}:00`).toISOString(), url: `${absolutizePath(base)}/${model.targetUid}`, allDay: false };
    }
  });

  const eventClicked = (info) => {
    if (info.event?.title?.includes("Open to Reserve")) {
      const selectedDate = new Date(info.event.start);
      setSelectedDate(selectedDate);
      const formattedDate = selectedDate.toLocaleDateString('en-CA');
      ParkApi.openAvailable({ start_date: formattedDate, end_date: formattedDate }).then(data => setSelectedPlaces(data));
      handleReserveClick(info.event.extendedProps.placeId, selectedDate);
    }
  }

  calendarEvents.push(...reservableDates.map(entry => ({ id: `reserve-${entry.date}`, title: "● Open to Reserve", start: entry.date, allDay: true })));

  return (
    <TopLevelPage className="Calendar" modelId={feedItemId} base="/community-calendar">
      <div className="row calendar-section justify-content-center mt-3">
        <div className="col">
          <FullCalendar plugins={[dayGridPlugin, interactionPlugin, timeGridPlugin]} initialView="dayGridMonth" timeZone="local" headerToolbar={{ left: 'title', right: 'today prev,next' }} datesSet={handleDatesSet} dateClick={(info) => setSelectedDate(new Date(info.dateStr))} eventClick={eventClicked} events={calendarEvents} height="auto" />
        </div>
      </div>
      { selectedPlaces.length > 0 &&
        <section>
          <div className="row justify-content-center">
            <div className="col-md-8">
              <h1 className="section-heading-lowercase my-3">Available Amenity Reservations</h1>
            </div>
          </div>
          <div className="row">
            { selectedPlaces.map((place, index) => (
                <div key={index} className="col-sm-6 col-lg-4 col-xl-3">
                  <ParkCard park={place} base="/community-calendar" selectedDate={selectedDate instanceof Date ? format(selectedDate, "yyyy-MM-dd") : format(new Date(), "yyyy-MM-dd")} />
                </div>
              ))
            }
          </div>
        </section>
      }
    </TopLevelPage>
  );
};

export default withErrorBoundary(connected(CalendarView));
